import { createIndexedDbStore, createNonPersistentStore } from 'lib/zustand/store';
import type { VoiceInfo } from 'modules/sdk/lib';

export enum UserSettingsEnum {
  AUTO_HIDE_INTERFACE = 'autoHideInterface',
  AUTO_SCROLL = 'autoScroll',
  AUTO_SPEED_UP = 'autoSpeedUp',
  CLICK_TO_LISTEN = 'clickToListen',
  CURRENT_VOICE = 'currentVoice',
  DARK_MODE = 'darkMode',
  FILES_SUGGESTIONS = 'filesSuggestions',
  PLAYBACK_SPEED = 'playbackSpeed',
  RECENT_VOICES = 'recentVoices',
  SKIP_BRACES = 'braces',
  SKIP_BRACKETS = 'brackets',
  SKIP_CITATIONS = 'citations',
  SKIP_ENHANCED = 'enhancedSkipping',
  SKIP_FOOTERS = 'footers',
  SKIP_FOOTNOTES = 'footnotes',
  SKIP_HEADERS = 'headers',
  SKIP_PARENTHESES = 'parentheses',
  SKIP_URLS = 'urls',
  SKIP_FORMULAS = 'formulas',
  SKIP_TABLES = 'tables',
  WEB_CURRENT_VOICE = 'webCurrentVoice'
}

export type UserSettingsType = {
  [UserSettingsEnum.AUTO_HIDE_INTERFACE]?: boolean;
  [UserSettingsEnum.AUTO_SCROLL]?: boolean;
  [UserSettingsEnum.AUTO_SPEED_UP]?: boolean;
  [UserSettingsEnum.CLICK_TO_LISTEN]?: boolean;
  [UserSettingsEnum.CURRENT_VOICE]?: VoiceInfo;
  [UserSettingsEnum.DARK_MODE]?: boolean;
  [UserSettingsEnum.FILES_SUGGESTIONS]?: boolean;
  [UserSettingsEnum.PLAYBACK_SPEED]?: number;
  [UserSettingsEnum.RECENT_VOICES]?: VoiceInfo[];
  [UserSettingsEnum.SKIP_BRACES]?: boolean;
  [UserSettingsEnum.SKIP_BRACKETS]?: boolean;
  [UserSettingsEnum.SKIP_CITATIONS]?: boolean;
  [UserSettingsEnum.SKIP_ENHANCED]?: boolean;
  [UserSettingsEnum.SKIP_FOOTERS]?: boolean;
  [UserSettingsEnum.SKIP_FOOTNOTES]?: boolean;
  [UserSettingsEnum.SKIP_HEADERS]?: boolean;
  [UserSettingsEnum.SKIP_PARENTHESES]?: boolean;
  [UserSettingsEnum.SKIP_URLS]?: boolean;
};

export enum SharedUserSettingsEnum {
  // library
  LIBRARY_IMPORT_SUGGEST_FILES = 'library.import.suggestFiles',

  // listening experience
  AUTO_HIDE_PLAYER = 'reader.playback.autoHidePlayer',
  AUTO_SCROLL = 'reader.playback.enableAutoScroll',
  AUTO_SPEED_UP = 'reader.playback.speed.autoIncrease',
  CLICK_TO_LISTEN = 'reader.playback.enableClickToListen',
  CURRENT_VOICE = 'reader.voice.current',
  HIGHLIGHTING = 'reader.playback.enableTextHighlight',
  PLAYBACK_SPEED = 'reader.playback.speed.wpm',
  RECENT_VOICES = 'reader.voice.recentlyUsed',
  FAVORITE_VOICES = 'reader.voice.favorites',

  // Platform-specific voice IDs
  WEB_CURRENT_VOICE = 'reader.voice.webApp.current',

  // daily goal
  DAILY_GOAL_ACTIVE_WEEKDAYS = 'dailyGoal.activeWeekdays',
  DAILY_GOAL_REMINDER_ENABLED = 'dailyGoal.reminder.enabled',
  DAILY_GOAL_REMINDER_TIME_OFFSET = 'dailyGoal.reminder.timeOffsetMinutes',
  DAILY_GOAL_TARGET_MINUTES = 'dailyGoal.targetMinutes',

  // auto-skip
  AUTO_SKIP_BRACES = 'reader.autoSkipContent.braces',
  AUTO_SKIP_BRACKETS = 'reader.autoSkipContent.brackets',
  AUTO_SKIP_CITATIONS = 'reader.autoSkipContent.citations',
  AUTO_SKIP_ENHANCED = 'reader.autoSkipContent.enhanced',
  AUTO_SKIP_FOOTERS = 'reader.autoSkipContent.footers',
  AUTO_SKIP_FOOTNOTES = 'reader.autoSkipContent.footnotes',
  AUTO_SKIP_HEADERS = 'reader.autoSkipContent.headers',
  AUTO_SKIP_PARENTHESES = 'reader.autoSkipContent.parentheses',
  AUTO_SKIP_URLS = 'reader.autoSkipContent.urls',
  AUTO_SKIP_FORMULAS = 'reader.autoSkipContent.formulas',
  AUTO_SKIP_TABLES = 'reader.autoSkipContent.tables'
}

export type SharedUserSettingsType = {
  // library
  [SharedUserSettingsEnum.LIBRARY_IMPORT_SUGGEST_FILES]?: boolean;

  // listening experience
  [SharedUserSettingsEnum.AUTO_HIDE_PLAYER]?: boolean;
  [SharedUserSettingsEnum.AUTO_SCROLL]?: boolean;
  [SharedUserSettingsEnum.AUTO_SPEED_UP]?: boolean;
  [SharedUserSettingsEnum.CLICK_TO_LISTEN]?: boolean;
  [SharedUserSettingsEnum.CURRENT_VOICE]?: string; // idQualified
  [SharedUserSettingsEnum.HIGHLIGHTING]?: boolean;
  [SharedUserSettingsEnum.PLAYBACK_SPEED]?: number;
  [SharedUserSettingsEnum.RECENT_VOICES]?: string[]; // idQualified
  [SharedUserSettingsEnum.FAVORITE_VOICES]?: string[]; // idQualified

  // Platform-specific voice IDs
  [SharedUserSettingsEnum.WEB_CURRENT_VOICE]?: string;

  // daily goal
  [SharedUserSettingsEnum.DAILY_GOAL_ACTIVE_WEEKDAYS]?: number[];
  [SharedUserSettingsEnum.DAILY_GOAL_REMINDER_ENABLED]?: boolean;
  [SharedUserSettingsEnum.DAILY_GOAL_REMINDER_TIME_OFFSET]?: number;
  [SharedUserSettingsEnum.DAILY_GOAL_TARGET_MINUTES]?: number;

  // auto-skip
  [SharedUserSettingsEnum.AUTO_SKIP_BRACES]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_BRACKETS]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_CITATIONS]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_ENHANCED]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_FOOTERS]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_FOOTNOTES]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_HEADERS]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_PARENTHESES]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_URLS]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_FORMULAS]?: boolean;
  [SharedUserSettingsEnum.AUTO_SKIP_TABLES]?: boolean;
};

export enum SettingsContext {
  WEB_APP = 'web_app',
  CHROME_EXTENSION = 'chrome_extension',
  IOS = 'ios',
  ANDROID = 'android'
}

export enum ErrorType {
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  INVALID_REQUEST = 'INVALID_REQUEST',
  VERSION_MISMATCH = 'VERSION_MISMATCH',
  NOT_MODIFIED = 'NOT_MODIFIED'
}

export interface ApiError {
  type: ErrorType;
  message: string;
  statusCode?: number;
}

export interface SettingsStoreState {
  isLoading: boolean;
  userSettings: { version: number; settings: Partial<SharedUserSettingsType> };
  syncStatus: {
    lastFetchTimestamp: number;
    pendingChanges: Array<{
      data: Record<string, Partial<UserSettingsType>>;
      timestamp: number;
    }>;
    syncErrors: {
      count: number;
      lastError: string;
      lastErrorTime: number;
    };
  };
}

export type PersistedSettingsStoreState = SettingsStoreState;

const INITIAL_STATE: SettingsStoreState = {
  isLoading: true,
  userSettings: { version: 0, settings: {} },
  syncStatus: {
    lastFetchTimestamp: 0,
    pendingChanges: [],
    syncErrors: {
      count: 0,
      lastError: '',
      lastErrorTime: 0
    }
  }
};

const getPersisted = () =>
  createIndexedDbStore<SettingsStoreState, PersistedSettingsStoreState>(() => INITIAL_STATE, {
    storageName: 'speechify-settings-store',
    version: 1,
    partialize: state => state
  });

const getNonPersisted = () =>
  createNonPersistentStore<SettingsStoreState>(() => INITIAL_STATE, {
    isListeningScreenStore: false
  });

export const useSettingsStore = typeof window !== 'undefined' ? getPersisted() : getNonPersisted();
